import { useEffect, useMemo, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Cpu,
  RefreshCw,
  Search,
  Server,
  Zap
} from 'lucide-react';
import API from '../services/api';

function queueLabel(type, queue, searchTerm) {
  if (type === 'pv' || queue === 'potluck') return 'PV';
  if (type === 'ai' || queue === 'all_items') return 'AI';
  if (type === 'search') return searchTerm || 'Keyword';
  return type || queue || '—';
}

function jobTitle(job) {
  const type = job.type || '';
  const queue = job.queue || '';
  const searchTerm = job.search_term || job.searchTerm || '';

  if (type === 'pv' || queue === 'potluck') return 'PV';
  if (type === 'ai' || queue === 'all_items') return `AI Seite ${job.page || 1}`;
  if (type === 'search') return searchTerm || 'Keyword Scan';

  return queueLabel(type, queue, searchTerm);
}

function formatTimeAgo(dateValue) {
  if (!dateValue) return '—';

  const date = new Date(dateValue);
  const diff = Date.now() - date.getTime();

  if (!Number.isFinite(diff)) return '—';

  const seconds = Math.max(0, Math.floor(diff / 1000));
  if (seconds < 60) return `vor ${seconds}s`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `vor ${minutes}m`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `vor ${hours}h`;

  return `vor ${Math.floor(hours / 24)}d`;
}


function formatAccountLabel(value) {
  const account = String(value || '').trim();
  return account ? `Account ${account}` : 'Account —';
}

function formatScanLog(log) {
  const type = log.type || log.queue || '';
  const page = log.page || 1;
  const products = Number(log.products_scanned) || 0;
  const inserted = Number(log.inserted_found) || 0;
  const searchTerm = log.search_term || log.searchTerm || '';

  if (type === 'pv' || log.queue === 'potluck') {
    return `PV gescannt · ${products} Produkte gefunden · ${inserted} neu`;
  }

  if (type === 'ai' || log.queue === 'all_items') {
    return `AI Seite ${page} gescannt · ${products} Produkte gefunden · ${inserted} neu`;
  }

  if (type === 'search') {
    const label = searchTerm || 'Keyword';
    if (products > 0 || inserted > 0) {
      return `${label} gescannt · ${products} Produkte gefunden · ${inserted} neu`;
    }
    return `${label} gescannt`;
  }

  return `${queueLabel(type, log.queue, searchTerm)} gescannt · ${products} Produkte gefunden · ${inserted} neu`;
}

function normalizeScannerId(scanner) {
  return (
    scanner.scanner_id ||
    scanner.scannerId ||
    scanner.id ||
    scanner.locked_by ||
    'unknown-scanner'
  );
}

function extractScannerDetails(scanner) {
  const scannerId = normalizeScannerId(scanner);

  return {
    scannerId,
    lastSeen:
      scanner.last_scan_at ||
      scanner.lastScanAt ||
      scanner.last_seen ||
      scanner.lastSeen ||
      scanner.updated_at ||
      null,
    accountName: scanner.scanner_account_name || scanner.scannerAccountName || null,
    scannerLabel: scanner.scanner_label || scanner.scannerLabel || null,
    extensionVersion: scanner.extension_version || scanner.extensionVersion || null,
    queue: scanner.queue || null,
    page: scanner.page || null,
    categoryId: scanner.category_id || scanner.categoryId || null,
    productsScanned: scanner.products_scanned || scanner.productsScanned || null,
    totalNewFound: scanner.total_new_found ?? scanner.totalNewFound ?? 0,
    todayNewFound: scanner.today_new_found ?? scanner.todayNewFound ?? 0,
    totalLoggedJobs: scanner.total_logged_jobs ?? scanner.totalLoggedJobs ?? 0,
    todayLoggedJobs: scanner.today_logged_jobs ?? scanner.todayLoggedJobs ?? 0,
    todayProductsScanned: scanner.today_products_scanned ?? scanner.todayProductsScanned ?? 0,
    currentJob: scanner.current_job || scanner.currentJob || scanner.currentJobType || scanner.current_job_type || null,
    allowedTypes: scanner.allowed_types || scanner.allowedTypes || [],
    status: scanner.status || 'aktiv',
    raw: scanner
  };
}

export default function ScannerMonitor() {
  const [activeScanners, setActiveScanners] = useState([]);
  const [scannerCounts, setScannerCounts] = useState({
    activeWorkerCount: 0,
    recentScannerCount: 0,
    connectedCount: 0
  });
  const [jobs, setJobs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [error, setError] = useState('');

  const loadMonitorData = async () => {
    try {
      const [scannersRes, jobsRes, logsRes] = await Promise.all([
        API.get('/scan/scanners/active?minutes=2'),
        API.get('/scan/jobs'),
        API.get('/scan/logs/recent?limit=10')
      ]);

      const scannerData = scannersRes.data || {};
      const scannerRows =
        scannerData.recentScanners ||
        scannerData.activeWorkers ||
        scannerData.scanners ||
        scannerData.rows ||
        [];

      const jobRows =
        jobsRes.data?.jobs ||
        jobsRes.data?.rows ||
        [];

      const logRows =
        logsRes.data?.logs ||
        logsRes.data?.rows ||
        [];

      setActiveScanners(Array.isArray(scannerRows) ? scannerRows.map(extractScannerDetails) : []);
      setScannerCounts({
        activeWorkerCount: Number(scannerData.activeWorkerCount) || 0,
        recentScannerCount: Number(scannerData.recentScannerCount) || 0,
        connectedCount: Number(scannerData.connectedCount ?? scannerData.count) || 0
      });
      setJobs(Array.isArray(jobRows) ? jobRows : []);
      setLogs(Array.isArray(logRows) ? logRows : []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Monitor konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMonitorData();

    if (!autoRefresh) return undefined;

    const timer = setInterval(loadMonitorData, 5000);
    return () => clearInterval(timer);
  }, [autoRefresh]);

  const stats = useMemo(() => {
    const enabledJobs = jobs.filter((job) => job.enabled !== false);
    const pvJobs = enabledJobs.filter((job) => job.type === 'pv');
    const aiJobs = enabledJobs.filter((job) => job.type === 'ai');
    const categoryJobs = enabledJobs.filter((job) => job.type === 'category');
    const searchJobs = enabledJobs.filter((job) => job.type === 'search');
    const lockedJobs = enabledJobs.filter((job) => Boolean(job.locked_by || job.lockedBy));

    const aiPages = new Set(aiJobs.map((job) => Number(job.page)).filter(Boolean));
    const categoryIds = new Set(categoryJobs.map((job) => String(job.category_id || job.categoryId || '').trim()).filter(Boolean));
    const categoryPages = new Set(categoryJobs.map((job) => `${job.category_id || job.categoryId || '-'}:${job.page || 1}`));
    const searchTerms = new Set(searchJobs.map((job) => String(job.search_term || job.searchTerm || '').trim()).filter(Boolean));

    const recentNew = logs.reduce((sum, log) => sum + (Number(log.inserted_found) || 0), 0);
    const recentScanned = logs.reduce((sum, log) => sum + (Number(log.products_scanned) || 0), 0);

    return {
      activeScannerCount: scannerCounts.recentScannerCount || activeScanners.length,
      activeWorkerCount: scannerCounts.activeWorkerCount || 0,
      connectedScannerCount: scannerCounts.connectedCount || activeScanners.length,
      enabledJobCount: enabledJobs.length,
      pvJobCount: pvJobs.length,
      aiJobCount: aiJobs.length,
      categoryJobCount: categoryJobs.length,
      searchJobCount: searchJobs.length,
      lockedJobCount: lockedJobs.length,
      aiPageCount: aiPages.size,
      categoryCount: categoryIds.size,
      categoryPageCount: categoryPages.size,
      searchTermCount: searchTerms.size,
      recentNew,
      recentScanned
    };
  }, [activeScanners, scannerCounts, jobs, logs]);

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.loading}>Lade Scanner Monitor...</div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Scanner Monitor</h1>
          <p style={styles.subtitle}>Live-Übersicht für echte Scanner-Geräte und letzte Scans</p>
        </div>

        <div style={styles.actions}>
          <label style={styles.autoRefreshLabel}>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            <span>Auto-Refresh 5s</span>
          </label>

          <button onClick={loadMonitorData} style={styles.refreshBtn}>
            <RefreshCw size={14} />
            Aktualisieren
          </button>
        </div>
      </div>

      {error && (
        <div style={styles.error}>
          <AlertTriangle size={16} />
          {error}
        </div>
      )}

      <div style={styles.statsGrid}>
        <StatCard
          icon={<Server size={20} />}
          label="Scanner aktiv"
          value={stats.activeScannerCount}
          sub={`${stats.connectedScannerCount} verbunden · ${stats.activeWorkerCount} arbeiten gerade`}
          accent="#22c55e"
        />
        <StatCard
          icon={<Cpu size={20} />}
          label="Aktive Jobs"
          value={stats.enabledJobCount}
          sub={`AI ${stats.aiPageCount} Seiten · Kat ${stats.categoryCount} Kategorien · Keywords ${stats.searchTermCount}`}
          accent="#3b82f6"
        />
        <StatCard
          icon={<Zap size={20} />}
          label="Neu in letzten Logs"
          value={stats.recentNew}
          sub={`${stats.recentScanned} Produkte gescannt`}
          accent="#f97316"
        />
        <StatCard
          icon={<Search size={20} />}
          label="Keyword/Brand Jobs"
          value={stats.searchJobCount}
          accent="#a855f7"
        />
      </div>

      <div style={styles.grid}>
        <section style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={styles.cardTitleWrap}>
              <Activity size={18} />
              <h2 style={styles.cardTitle}>Scanner-Geräte</h2>
            </div>
            <span style={styles.pill}>{stats.activeScannerCount} aktiv · {stats.connectedScannerCount} verbunden</span>
          </div>

          {activeScanners.length === 0 ? (
            <EmptyState text="Keine echten Scan-Aktivitäten in den letzten 2 Minuten" />
          ) : (
            <div style={styles.list}>
              {activeScanners.map((scanner) => (
                <ScannerCard key={scanner.scannerId} scanner={scanner} jobs={jobs} />
              ))}
            </div>
          )}
        </section>

        <section style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={styles.cardTitleWrap}>
              <Clock size={18} />
              <h2 style={styles.cardTitle}>Letzte Scans</h2>
            </div>
            <span style={styles.pill}>{logs.length}</span>
          </div>

          {logs.length === 0 ? (
            <EmptyState text="Noch keine Scan-Logs vorhanden" />
          ) : (
            <div style={styles.list}>
              {logs.map((log, index) => (
                <div key={`${log.id || index}-${log.created_at || index}`} style={styles.logItem}>
                  <div style={styles.logText}>{formatScanLog(log)}</div>
                  <div style={styles.logMeta}>
                    {formatTimeAgo(log.created_at || log.createdAt || log.scanned_at || log.last_scanned)}
                    {log.duration_ms ? ` · ${log.duration_ms}ms` : ''}
                    {' · '}{formatAccountLabel(log.scanner_account_name || log.scannerAccountName)}
                    {log.scanner_id ? ` · ${log.scanner_id}` : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      <section style={styles.card}>
        <div style={styles.cardHeader}>
          <div style={styles.cardTitleWrap}>
            <CheckCircle2 size={18} />
            <h2 style={styles.cardTitle}>System-Übersicht</h2>
          </div>
          <span style={styles.pill}>{stats.lockedJobCount} läuft · {stats.enabledJobCount} aktiv</span>
        </div>

        <SystemOverview stats={stats} />
      </section>
    </div>
  );
}

function SystemOverview({ stats }) {
  const rows = [
    {
      label: 'AI Scan',
      value: `${stats.aiPageCount || 0} Seiten`,
      detail: stats.aiJobCount ? `${stats.aiJobCount} aktive AI-Jobs im Hintergrund` : 'keine AI-Jobs aktiv',
      accent: '#3b82f6'
    },
    {
      label: 'Kategorie Scan',
      value: `${stats.categoryCount || 0} Kategorien`,
      detail: stats.categoryJobCount ? `${stats.categoryPageCount || stats.categoryJobCount} Kategorie-Seiten aktiv` : 'keine Kategorie-Jobs aktiv',
      accent: '#f97316'
    },
    {
      label: 'PV Scan',
      value: `${stats.pvJobCount || 0} globale Jobs`,
      detail: 'PV läuft aktuell lokal pro Chrome-Profil und wird nicht über Job-Karten gesteuert',
      accent: '#22c55e'
    },
    {
      label: 'Keyword / Search',
      value: `${stats.searchTermCount || stats.searchJobCount || 0} Keywords`,
      detail: stats.searchJobCount ? `${stats.searchJobCount} Such-Jobs aktiv` : 'keine Such-Jobs aktiv',
      accent: '#a855f7'
    }
  ];

  return (
    <div style={styles.systemGrid}>
      {rows.map((row) => (
        <div key={row.label} style={{ ...styles.systemCard, borderColor: row.accent }}>
          <div style={{ ...styles.systemDot, background: row.accent }} />
          <div>
            <div style={styles.systemLabel}>{row.label}</div>
            <div style={styles.systemValue}>{row.value}</div>
            <div style={styles.systemDetail}>{row.detail}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function StatCard({ icon, label, value, sub, accent }) {
  return (
    <div style={{ ...styles.statCard, borderColor: accent }}>
      <div style={{ ...styles.statIcon, color: accent }}>{icon}</div>
      <div>
        <div style={styles.statLabel}>{label}</div>
        <div style={{ ...styles.statValue, color: accent }}>{value}</div>
        {sub && <div style={styles.statSub}>{sub}</div>}
      </div>
    </div>
  );
}

function ScannerCard({ scanner, jobs }) {
  const lockedJobs = jobs.filter((job) => {
    const lockedBy = job.locked_by || job.lockedBy || '';
    return lockedBy && lockedBy === scanner.scannerId;
  });

  const latestJob = lockedJobs[0] || null;
  const hasAccount = Boolean(scanner.accountName || scanner.scannerLabel);
  const displayName = scanner.accountName || scanner.scannerLabel || 'Scanner ohne Account';
  const lastActivity = scanner.lastSeen;
  const jobLabel = scanner.queue
    ? `${queueLabel(null, scanner.queue, null)}${scanner.page ? ` Seite ${scanner.page}` : ''}`
    : latestJob
      ? jobTitle(latestJob)
      : 'wartet';

  return (
    <div style={styles.scannerCard}>
      <div style={styles.scannerTop}>
        <div>
          <div style={styles.scannerName}>{displayName}</div>
          <div style={styles.scannerMeta}>
            Letzter Scan: {formatTimeAgo(lastActivity)}
            {!hasAccount ? ' · Account in Extension nicht gewählt' : ''}
            {scanner.extensionVersion ? ` · Version ${scanner.extensionVersion}` : ' · Version —'}
            {scanner.scannerId && ` · ${scanner.scannerId}`}
          </div>
        </div>

        <span style={styles.activeBadge}>scannt</span>
      </div>

      <div style={styles.scannerStats}>
        <InfoBlock label="Letzter Job" value={jobLabel} />
        <InfoBlock label="Neue ASINs heute" value={scanner.todayNewFound || 0} />
        <InfoBlock label="Neue ASINs gesamt" value={scanner.totalNewFound || 0} />
        <InfoBlock label="Produkte letzter Scan" value={scanner.productsScanned || '—'} />
      </div>
    </div>
  );
}

function InfoBlock({ label, value }) {
  return (
    <div style={styles.infoBlock}>
      <div style={styles.infoLabel}>{label}</div>
      <div style={styles.infoValue}>{value ?? '—'}</div>
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div style={styles.empty}>
      <Activity size={28} />
      <div>{text}</div>
    </div>
  );
}

const styles = {
  page: {
    padding: 24,
    background: 'var(--bg)',
    minHeight: '100vh',
    color: 'var(--text)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
    flexWrap: 'wrap',
    marginBottom: 24
  },
  title: {
    fontSize: 26,
    lineHeight: 1.1,
    fontWeight: 800,
    fontFamily: 'Syne',
    margin: 0,
    color: 'var(--text)'
  },
  subtitle: {
    marginTop: 6,
    fontSize: 14,
    color: 'var(--text3)'
  },
  actions: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  autoRefreshLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    color: 'var(--text3)',
    fontSize: 13
  },
  refreshBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 14px',
    border: '1px solid var(--border)',
    borderRadius: 8,
    background: 'var(--bg2)',
    color: 'var(--text2)',
    cursor: 'pointer'
  },
  loading: {
    padding: 60,
    textAlign: 'center',
    color: 'var(--text3)'
  },
  error: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    marginBottom: 20,
    borderRadius: 10,
    border: '1px solid rgba(239,68,68,0.35)',
    background: 'rgba(239,68,68,0.1)',
    color: '#ef4444'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
    gap: 16,
    marginBottom: 20
  },
  statCard: {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    padding: 16,
    background: 'var(--bg2)',
    border: '2px solid',
    borderRadius: 12
  },
  statIcon: {
    width: 42,
    height: 42,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255,255,255,0.05)'
  },
  statLabel: {
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    color: 'var(--text3)',
    marginBottom: 4
  },
  statValue: {
    fontSize: 24,
    lineHeight: 1,
    fontWeight: 800
  },
  statSub: {
    marginTop: 5,
    fontSize: 12,
    color: 'var(--text3)'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
    gap: 20,
    marginBottom: 20
  },
  card: {
    background: 'var(--bg2)',
    border: '1px solid var(--border)',
    borderRadius: 14,
    overflow: 'hidden'
  },
  cardHeader: {
    padding: '14px 18px',
    borderBottom: '1px solid var(--border)',
    background: 'var(--bg3)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12
  },
  cardTitleWrap: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    color: 'var(--accent)'
  },
  cardTitle: {
    margin: 0,
    fontSize: 16,
    fontWeight: 800,
    fontFamily: 'Syne',
    color: 'var(--text)'
  },
  pill: {
    padding: '4px 9px',
    borderRadius: 999,
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid var(--border)',
    color: 'var(--text3)',
    fontSize: 12
  },
  list: {
    padding: 16,
    display: 'grid',
    gap: 12
  },
  scannerCard: {
    padding: 14,
    border: '1px solid var(--border)',
    borderRadius: 12,
    background: 'var(--bg3)'
  },
  scannerTop: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 12
  },
  scannerName: {
    fontSize: 13,
    fontWeight: 700,
    color: 'var(--text)',
    wordBreak: 'break-all',
    fontFamily: 'monospace'
  },
  scannerMeta: {
    marginTop: 4,
    fontSize: 12,
    color: 'var(--text3)'
  },
  activeBadge: {
    height: 'fit-content',
    padding: '4px 8px',
    borderRadius: 999,
    background: 'rgba(34,197,94,0.12)',
    color: '#22c55e',
    border: '1px solid rgba(34,197,94,0.3)',
    fontSize: 12
  },
  scannerStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
    gap: 10
  },
  logItem: {
    padding: 12,
    border: '1px solid var(--border)',
    borderRadius: 10,
    background: 'var(--bg3)'
  },
  logText: {
    fontSize: 14,
    fontWeight: 700,
    color: 'var(--text2)'
  },
  logMeta: {
    marginTop: 5,
    fontSize: 12,
    color: 'var(--text3)'
  },
  jobsGrid: {
    padding: 16,
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: 12
  },
  systemGrid: {
    padding: 16,
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: 12
  },
  systemCard: {
    display: 'flex',
    gap: 12,
    alignItems: 'flex-start',
    padding: 14,
    border: '2px solid',
    borderRadius: 12,
    background: 'var(--bg3)'
  },
  systemDot: {
    width: 10,
    height: 10,
    borderRadius: 999,
    marginTop: 5,
    flex: '0 0 auto'
  },
  systemLabel: {
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    color: 'var(--text3)',
    marginBottom: 5
  },
  systemValue: {
    fontSize: 17,
    fontWeight: 800,
    color: 'var(--text)'
  },
  systemDetail: {
    marginTop: 5,
    fontSize: 12,
    color: 'var(--text3)',
    lineHeight: 1.35
  },
  jobCard: {
    padding: 14,
    border: '1px solid var(--border)',
    borderRadius: 12,
    background: 'var(--bg3)'
  },
  jobTop: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 12
  },
  jobTitle: {
    fontSize: 15,
    fontWeight: 800,
    color: 'var(--text)'
  },
  jobMeta: {
    marginTop: 4,
    fontSize: 12,
    color: 'var(--text3)'
  },
  jobBadge: {
    height: 'fit-content',
    padding: '4px 8px',
    borderRadius: 999,
    fontSize: 12,
    border: '1px solid currentColor'
  },
  jobDetails: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 10
  },
  lockInfo: {
    marginTop: 10,
    padding: 8,
    borderRadius: 8,
    background: 'rgba(249,115,22,0.08)',
    color: '#fb923c',
    fontSize: 11,
    wordBreak: 'break-all',
    fontFamily: 'monospace'
  },
  infoBlock: {
    minWidth: 0
  },
  infoLabel: {
    fontSize: 10,
    color: 'var(--text3)',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 3
  },
  infoValue: {
    fontSize: 13,
    fontWeight: 700,
    color: 'var(--text2)',
    wordBreak: 'break-word'
  },
  empty: {
    padding: 44,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
    color: 'var(--text3)',
    textAlign: 'center'
  }
};
